import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  Stack,
  Zoom,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import useSubscription from '../../../hooks/useSubscription';
import { AppConfig } from '../../../core';

export function CheckOutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [isProcessing, setIsProcessing] = useState(true);

  const { sub, loading: subLoading, refetch } = useSubscription();

  useEffect(() => {
    if (!sessionId) {
      navigate('/app/settings');
      return;
    }

    // Refetch subscription to get updated status from Firestore
    const fetchUpdatedStatus = async () => {
      try {
        // Wait a bit for webhook to process (webhooks can take 1-2 seconds)
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await refetch();
        setIsProcessing(false);
      } catch (error) {
        console.error('Error fetching updated status:', error);
        setIsProcessing(false);
      }
    };

    fetchUpdatedStatus();
  }, [sessionId, navigate, refetch]);

  if (isProcessing || subLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          mt: '64px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isSubscription =
    sub?.subscription_id !== null && sub?.subscription_id !== undefined;
  const isOneTime = sub?.oneoff_setup_paid === true;

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: { xs: 8, md: 12 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 3,
        }}
      >
        <Zoom in={true} style={{ transitionDelay: '300ms' }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0 0 40px rgba(76, 175, 80, 0.2)',
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 60, color: 'success.main' }}
            />
          </Box>
        </Zoom>
        {isSubscription && (
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              Your Legacy is Secured
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400, px: 2 }}
            >
              Thank you for choosing us to preserve your family story. Your
              subscription is now active, and your digital home is ready.
            </Typography>

            <Box
              sx={{
                width: '100%',
                p: 3,
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                mt: 2,
              }}
            >
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="success.main"
                  >
                    Payment Successful
                  </Typography>
                </Box>
                {sub?.current_period_end && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Next billing date
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {new Date(sub?.current_period_end).toLocaleDateString(
                        'en-US',
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        },
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        )}

        {isOneTime && !isSubscription && (
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              Setup Support Purchased!
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400, px: 2 }}
            >
              Your one-time setup support has been confirmed.
            </Typography>
            <Box
              sx={{
                width: '100%',
                p: 3,
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
                mt: 2,
              }}
            >
              <Stack spacing={2}>
                <Typography>What happens next</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Within 24 hours
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    Our team will contact you via email
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="left"
                  >
                    Schedule session
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    We'll find a convenient time for your dedicated setup
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="left"
                  >
                    Custom configuration
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    Receive personalised assistance tailored to your needs
                  </Typography>
                </Box>
                {sub?.oneoff_setup_paid_at && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Purchase Date
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {new Date(sub.oneoff_setup_paid_at).toLocaleDateString(
                        'en-US',
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        },
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        )}
        {/* Fallback for edge cases */}
        {!isSubscription && !isOneTime && (
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              Your payment has been processed successfully.
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400, px: 2 }}
            >
              You now have access to premium features.
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontWeight: 400, px: 2 }}
            >
              If you don't see your updates immediately, please refresh your
              account page in a moment.
            </Typography>
          </Box>
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mt: 4, width: '100%' }}
        >
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => navigate('/app')}
            sx={{ borderRadius: 3, py: 1.5, fontWeight: 700 }}
          >
            Go to Dashboard
          </Button>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            onClick={() => navigate('/app/settings')}
            sx={{ borderRadius: 3, py: 1.5, fontWeight: 700 }}
          >
            Go to Account
          </Button>
        </Stack>
        <Box>
          <Typography variant="body1" color="text" sx={{ mt: 2 }}>
            A confirmation email and receipt have been sent to your inbox.
          </Typography>
          <Typography variant="subtitle1" color="info" sx={{ mt: 2 }}>
            Questions? Contact us at{' '}
            <strong>{AppConfig.appContactEmail}</strong>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}
